import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { capitalizeAllFirstLetters, capitalizeFirstLetter, formateDate, formateDateWithTime, getBrandId, getOutletId, removeHyphens } from '../../helpers/commonFunctions';
import Breadcrums from '../../components/company_panel/common/breadcrumbs';
import Loader from '../../components/company_panel/common/loader'
import { AllWasteReason, WasteDetails } from '../../services/company_panel/wasteManagement.services';


const WasteManagementView = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [showDefault, setShowDefault] = useState({});
    const breadcrumbs = [{ title: "Waste Management", url: `/company/store_manager/waste_management/list/${params?.pgno || 1}` }, { title: "View Waste", url: "" }];
    const [isBrandChanged, setIsBrandChanged] = useState(false);
    const [currentOutlet] = useState(getOutletId());
    const [isOutletChanged, setIsOutletChanged] = useState(false);
    const [reasonMap, setReasonMap] = useState({});

    useMemo(() => {
        document.addEventListener('brand-changed', function () {
            setIsBrandChanged(!isBrandChanged);
        });
        document.addEventListener('outlet-changed', function () {
            setIsOutletChanged(!isOutletChanged);
        });
        setTimeout(() => {
            if (currentOutlet != getOutletId()) {
                navigate(`/company/store_manager/waste_management/list/${params?.pgno || 1}`);
            }
        }, 200);
    });

    useEffect(() => {
        WasteDetails({ id: params.id }).then(data => {
            setShowDefault(data && data.data && data.data.response ? data.data.response : [])
        }).catch(error => {
            console.log("error=====>", error)
        })
    }, [])

    // reason_for_waste comes as an id only, so resolve id -> name client-side.
    useEffect(() => {
        AllWasteReason({ brand_id: getBrandId() }).then(data => {
            const rows = data?.data?.response || [];
            const map = {};
            rows.forEach(row => { map[row.id] = row.reason; });
            setReasonMap(map);
        }).catch(error => { console.log("error=====>", error); });
    }, [isBrandChanged]);

    // Brand / outlet names for the print header come back with the record.
    const brandName = showDefault?.brand?.name || '';
    const outletLabel = showDefault?.outlet?.outlet_name
        ? `${showDefault.outlet.outlet_name}${showDefault.outlet.short_code ? ` (${showDefault.outlet.short_code})` : ''}`
        : '';

    const getWastedItemName = (item) => {
        const name = item?.item_type === 'raw-material' ? item?.inventory_name : item?.menu_item_name;
        return name ? capitalizeAllFirstLetters(name) : 'N/A';
    };

    const getReasonName = (item) => {
        const reason = reasonMap[item?.reason_for_waste];
        return reason ? capitalizeAllFirstLetters(reason) : 'N/A';
    };

    const wastedItems = showDefault?.wasted_items || [];
    const totalAmountLoss = wastedItems.reduce((sum, it) => sum + Number(it?.amount_loss || 0), 0);

    // Print the record as an A4 sheet. Rendered into a hidden iframe rather than a
    // popup window so pop-up blockers can't swallow it.
    const handlePrint = () => {
        const escapeHtml = (value) => String(value ?? '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

        const rows = wastedItems.length
            ? wastedItems.map(it => `
                <tr>
                    <td>${escapeHtml(it?.item_type ? capitalizeAllFirstLetters(removeHyphens(it.item_type)) : 'N/A')}</td>
                    <td>${escapeHtml(getWastedItemName(it))}</td>
                    <td>${escapeHtml(it?.quantity ?? 'N/A')}</td>
                    <td>${escapeHtml(it?.uom ? capitalizeFirstLetter(it.uom) : '-')}</td>
                    <td>${escapeHtml(it?.amount_loss != null && it?.amount_loss !== '' ? `${it.amount_loss} SAR` : 'N/A')}</td>
                    <td>${escapeHtml(getReasonName(it))}</td>
                    <td>${escapeHtml(it?.order_id ? `#order ${it.order_id}` : '-')}</td>
                    <td>${escapeHtml(it?.remarks || '-')}</td>
                </tr>`).join('')
            : '<tr><td colspan="8" style="text-align:center">No wasted items.</td></tr>';

        const totalRow = wastedItems.length
            ? `<tfoot><tr><td colspan="4" style="text-align:right">Total</td><td>${escapeHtml(totalAmountLoss.toFixed(2))} SAR</td><td colspan="3"></td></tr></tfoot>`
            : '';

        const html = `
            <html>
                <head>
                    <title>Waste #${escapeHtml(showDefault?.id || '')}</title>
                    <style>
                        @page { size: A4; margin: 14mm; }
                        * { box-sizing: border-box; }
                        body { font-family: Arial, Helvetica, sans-serif; color: #2c1d37; font-size: 12px; margin: 0; }
                        .head { border-bottom: 2px solid #2f9bff; padding-bottom: 10px; margin-bottom: 16px; }
                        .brand { font-size: 20px; font-weight: 700; margin: 0; }
                        .outlet { font-size: 13px; color: #555; margin: 2px 0 0; }
                        .title { font-size: 15px; font-weight: 700; margin: 14px 0 8px; }
                        table { width: 100%; border-collapse: collapse; }
                        .meta td { padding: 5px 8px; border: 1px solid #e8e8e8; width: 32%; vertical-align: top; }
                        .meta .lbl { font-weight: 700; background: #f7f7fa; width: 18%; }
                        .items { margin-top: 16px; }
                        .items th, .items td { border: 1px solid #e8e8e8; padding: 6px 8px; text-align: left; }
                        .items thead th { background: #f7f7fa; font-weight: 700; }
                        .items tfoot td { font-weight: 700; background: #f7f7fa; }
                        .foot { margin-top: 18px; font-size: 11px; color: #888; text-align: right; }
                    </style>
                </head>
                <body>
                    <div class="head">
                        <p class="brand">${escapeHtml(brandName ? capitalizeAllFirstLetters(brandName) : 'N/A')}</p>
                        <p class="outlet">${escapeHtml(outletLabel ? capitalizeAllFirstLetters(outletLabel) : 'N/A')}</p>
                    </div>
                    <div class="title">Waste Details</div>
                    <table class="meta">
                        <tr>
                            <td class="lbl">Id</td><td>${escapeHtml(showDefault?.id || 'N/A')}</td>
                            <td class="lbl">Business Date</td><td>${escapeHtml(showDefault?.business_date ? formateDate(showDefault.business_date, false) : 'N/A')}</td>
                        </tr>
                        <tr>
                            <td class="lbl">Amount Loss</td><td>${escapeHtml(showDefault?.amount_loss != null && showDefault?.amount_loss !== '' ? `${showDefault.amount_loss} SAR` : 'N/A')}</td>
                            <td class="lbl">Created At</td><td>${escapeHtml(showDefault?.created_at ? formateDateWithTime(showDefault.created_at, true) : 'N/A')}</td>
                        </tr>
                    </table>
                    <table class="items">
                        <thead>
                            <tr>
                                <th>Item Type</th><th>Item Name</th><th>Quantity</th><th>Unit Of Usage</th>
                                <th>Amount Loss</th><th>Reason</th><th>Order</th><th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                        ${totalRow}
                    </table>
                    <div class="foot">Printed on ${escapeHtml(formateDateWithTime(new Date(), true))}</div>
                </body>
            </html>`;

        const frame = document.createElement('iframe');
        frame.style.position = 'fixed';
        frame.style.right = '0';
        frame.style.bottom = '0';
        frame.style.width = '0';
        frame.style.height = '0';
        frame.style.border = '0';
        document.body.appendChild(frame);

        const frameDoc = frame.contentWindow.document;
        frameDoc.open();
        frameDoc.write(html);
        frameDoc.close();

        frame.contentWindow.focus();
        frame.contentWindow.print();
        // Give the dialog time to take the document before dropping the frame.
        setTimeout(() => { document.body.removeChild(frame); }, 1000);
    };

    return (
        <>
            <div className="page-header d-flex align-items-center">
                <div className="page-header-left">
                    <Breadcrums data={breadcrumbs} />
                    <h4>View Waste</h4>
                </div>
                <div className="page-header-right ms-auto">
                    {showDefault && Object.keys(showDefault).length > 0 ?
                        <button className="btn btn-primary" onClick={handlePrint}>
                            <i className="ti ti-printer me-1" aria-hidden="true"></i> Print
                        </button>
                        : ''}
                </div>
            </div>
            <div className="card custom-card">
                {showDefault && Object.keys(showDefault).length > 0 ?
                    <div className="card-body">
                        <div className='row mb-4'>
                            <div className='col-lg-3 mb-2'>
                                <p className='mb-0'><span className='fw-bold'>Id: </span>{showDefault?.id || 'N/A'}</p>
                            </div>
                            <div className='col-lg-3 mb-2'>
                                <label className='fw-bold'>Business Date</label>
                                <p className='mb-0'>{showDefault?.business_date ? formateDate(showDefault?.business_date, false) : 'N/A'}</p>
                            </div>
                            <div className='col-lg-3 mb-2'>
                                <label className='fw-bold'>Amount Loss</label>
                                <p className='mb-0'>{showDefault?.amount_loss != null && showDefault?.amount_loss !== '' ? `${showDefault.amount_loss} SAR` : 'N/A'}</p>
                            </div>
                            <div className='col-lg-3 mb-2'>
                                <label className='fw-bold'>Created At</label>
                                <p className='mb-0'>{showDefault?.created_at ? formateDateWithTime(showDefault?.created_at, true) : 'N/A'}</p>
                            </div>
                        </div>
                        <div className="col-lg-12 form-group">
                            <table id="simple-table" className="table table-custom">
                                <thead>
                                    <tr>
                                        <th>Item Type</th>
                                        <th>Item Name</th>
                                        <th>Quantity</th>
                                        <th>Unit Of Usage</th>
                                        <th>Amount Loss</th>
                                        <th>Reason</th>
                                        <th>Order</th>
                                        <th>Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {wastedItems.length ? wastedItems.map((it, idx) => (
                                        <tr key={idx}>
                                            <td>{it?.item_type ? capitalizeAllFirstLetters(removeHyphens(it?.item_type)) : 'N/A'}</td>
                                            <td>{getWastedItemName(it)}</td>
                                            <td>{it?.quantity ?? 'N/A'}</td>
                                            <td>{it?.uom ? capitalizeFirstLetter(it?.uom) : '-'}</td>
                                            <td>{it?.amount_loss != null && it?.amount_loss !== '' ? `${it.amount_loss} SAR` : 'N/A'}</td>
                                            <td>{getReasonName(it)}</td>
                                            <td>{it?.order_id ? `#order ${it?.order_id}` : '-'}</td>
                                            <td>{it?.remarks ? it?.remarks : '-'}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={8} className='text-center'>No wasted items.</td>
                                        </tr>
                                    )}
                                </tbody>
                                {wastedItems.length ? (
                                    <tfoot>
                                        <tr>
                                            <td colSpan={4} className='text-end fw-bold'>Total</td>
                                            <td className='fw-bold'>{totalAmountLoss.toFixed(2)} SAR</td>
                                            <td colSpan={3}></td>
                                        </tr>
                                    </tfoot>
                                ) : null}
                            </table>
                        </div>
                    </div> : <Loader />}
            </div>
        </>
    )
}

export default WasteManagementView
